import React, { useEffect, useState } from 'react';
import { Alert, Form, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  FaBars,
  FaBell,
  FaChartLine,
  FaChevronRight,
  FaClipboardCheck,
  FaCommentDots,
  FaDumbbell,
  FaEllipsisH,
  FaGlobe,
  FaLayerGroup,
  FaMoon,
  FaPhoneAlt,
  FaRedoAlt,
  FaSearch,
  FaSignOutAlt,
  FaSun,
  FaStar,
  FaTimes,
  FaUsers,
  FaWalking,
} from 'react-icons/fa';
import { adminNavigationSections } from './adminNavigationConfig';
import { filterAdminNavigationSections, getAllowedPaths, readStoredAdminUser } from './adminAccessControl';
import { resolveOutletExperience } from './outletTypeOptions';
import { useTheme } from '../../context/ThemeContext';
import './Dashboard.css';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const POS_API_BASE_URL = process.env.REACT_APP_API_PRODUCT_URL || '';

const createEmptySummary = () => ({
  stats: {
    total_outlets: 0,
    total_members: 0,
    active_members: 0,
    today_checkins: 0,
    today_members_added: 0,
    pending_payments: 0,
    today_revenue: 0,
    monthly_revenue: 0,
    pt_sessions_this_month: 0,
  },
  activity_summary: MONTH_LABELS.map(label => ({
    label,
    checkins: 0,
    new_members: 0,
    pt_sessions: 0,
  })),
  sales_summary: MONTH_LABELS.map(label => ({
    label,
    approved_memberships: 0,
    pending_memberships: 0,
    pt_packages: 0,
  })),
  notifications: [],
  recent_activity: [],
  manager_contacts: [],
  featured_plan: null,
  experience: 'admin',
  actor_payment_summary: {
    today_transactions: 0,
    today_completed: 0,
    today_revenue: 0,
    pending_transactions: 0,
    this_month_transactions: 0,
    this_month_revenue: 0,
    recent_transactions: [],
  },
  actor_member_summary: {
    today_added: 0,
    this_month_added: 0,
    recent_members: [],
  },
  birthdays: {
    today: [],
    tomorrow: [],
    upcoming: [],
  },
  meta: {
    year: new Date().getFullYear(),
    generated_at: '',
    selected_outlet: 0,
  },
  user_scope: null,
});

const createEmptyPOSSummary = () => ({
  total_sales: 0,
  today_sales: 0,
  today_revenue: 0,
  yesterday_revenue: 0,
  weekly_revenue: 0,
  monthly_revenue: 0,
  transactions: [],
});

const normalizePOSSummary = payload => {
  const empty = createEmptyPOSSummary();

  return {
    ...empty,
    ...(payload || {}),
    transactions: Array.isArray(payload?.transactions) ? payload.transactions : empty.transactions,
  };
};

const parseDashboardJsonResponse = async response => {
  const raw = await response.text();
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return { message: raw };
  }
};

const normalizeSummary = payload => {
  const empty = createEmptySummary();

  return {
    ...empty,
    ...payload,
    stats: {
      ...empty.stats,
      ...(payload?.stats || {}),
    },
    activity_summary:
      Array.isArray(payload?.activity_summary) && payload.activity_summary.length > 0
        ? payload.activity_summary
        : empty.activity_summary,
    sales_summary:
      Array.isArray(payload?.sales_summary) && payload.sales_summary.length > 0
        ? payload.sales_summary
        : empty.sales_summary,
    notifications: Array.isArray(payload?.notifications) ? payload.notifications : empty.notifications,
    recent_activity: Array.isArray(payload?.recent_activity) ? payload.recent_activity : empty.recent_activity,
    manager_contacts: Array.isArray(payload?.manager_contacts) ? payload.manager_contacts : empty.manager_contacts,
    featured_plan: payload?.featured_plan || null,
    experience: payload?.experience || empty.experience,
    actor_payment_summary: {
      ...empty.actor_payment_summary,
      ...(payload?.actor_payment_summary || {}),
      recent_transactions: Array.isArray(payload?.actor_payment_summary?.recent_transactions)
        ? payload.actor_payment_summary.recent_transactions
        : empty.actor_payment_summary.recent_transactions,
    },
    actor_member_summary: {
      ...empty.actor_member_summary,
      ...(payload?.actor_member_summary || {}),
      recent_members: Array.isArray(payload?.actor_member_summary?.recent_members)
        ? payload.actor_member_summary.recent_members
        : empty.actor_member_summary.recent_members,
    },
    birthdays: {
      ...empty.birthdays,
      ...(payload?.birthdays || {}),
    },
    meta: {
      ...empty.meta,
      ...(payload?.meta || {}),
    },
    user_scope: payload?.user_scope || null,
  };
};

const isSuccessResponse = value => value === 0 || value === '0';

const formatNumber = value => Number(value || 0).toLocaleString('id-ID');

const formatCompactNumber = value =>
  new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value || 0));

const formatCurrency = value =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatCompactCurrency = value => {
  const amount = Number(value || 0);
  if (amount === 0) {
    return 'IDR 0';
  }

  return `IDR ${new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount)}`;
};

const dashboardTimezoneSuffixPattern = /(Z|[+-]\d{2}:\d{2})$/i;

const parseDashboardDateValue = (value, options = {}) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  const parseAsLocalWallClock = input => {
    const normalized = input
      .replace(dashboardTimezoneSuffixPattern, '')
      .replace(' ', 'T');
    const match = normalized.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/
    );

    if (!match) {
      return null;
    }

    const [, year, month, day, hour, minute, second = '0'] = match;
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return parseAsLocalWallClock(raw);
  }

  if (options.preferLocalWallClock && dashboardTimezoneSuffixPattern.test(raw)) {
    const now = Date.now();
    const diffMs = parsed.getTime() - now;
    if (diffMs > 60 * 1000 && diffMs <= 12 * 60 * 60 * 1000) {
      const localCandidate = parseAsLocalWallClock(raw);
      if (localCandidate && localCandidate.getTime() <= now + 60 * 1000) {
        return localCandidate;
      }
    }
  }

  return parsed;
};

const shouldPreferLocalWallClockForActivity = kind => kind !== 'birthday';

const formatDateTime = (value, options = {}) => {
  if (!value) {
    return 'Just now';
  }

  const date = parseDashboardDateValue(value, options);
  if (!date) {
    return value;
  }

  return new Intl.DateTimeFormat('id-ID', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const relativeTimeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const formatRelativeTime = (value, options = {}) => {
  if (!value) {
    return 'Just now';
  }

  const date = parseDashboardDateValue(value, options);
  if (!date) {
    return formatDateTime(value, options);
  }

  const timestamp = date.getTime();
  const diffInSeconds = Math.round((timestamp - Date.now()) / 1000);
  const absSeconds = Math.abs(diffInSeconds);

  if (absSeconds < 60) {
    return relativeTimeFormatter.format(diffInSeconds, 'second');
  }

  const diffInMinutes = Math.round(diffInSeconds / 60);
  if (Math.abs(diffInMinutes) < 60) {
    return relativeTimeFormatter.format(diffInMinutes, 'minute');
  }

  const diffInHours = Math.round(diffInSeconds / 3600);
  if (Math.abs(diffInHours) < 24) {
    return relativeTimeFormatter.format(diffInHours, 'hour');
  }

  const diffInDays = Math.round(diffInSeconds / 86400);
  if (Math.abs(diffInDays) < 7) {
    return relativeTimeFormatter.format(diffInDays, 'day');
  }

  return formatDateTime(value, options);
};

const formatPlanDuration = days => {
  const durationDays = Number(days || 0);
  if (durationDays <= 0) {
    return 'Flexible duration';
  }
  if (durationDays % 30 === 0) {
    const months = durationDays / 30;
    return `${months} month${months > 1 ? 's' : ''}`;
  }

  return `${durationDays} days`;
};

const formatPaymentStatusLabel = status => {
  switch (Number(status)) {
    case 1:
      return 'Completed';
    case 2:
      return 'Failed';
    default:
      return 'Pending';
  }
};

const getActivityAccent = kind => {
  switch (kind) {
    case 'birthday':
      return '#ff9d57';
    case 'payment':
      return '#4be89e';
    case 'pending':
      return '#ffb357';
    case 'member':
      return '#7e8cff';
    case 'warning':
      return '#ff7d7d';
    default:
      return '#c1ff5f';
  }
};

const getNotificationIcon = kind => {
  switch (kind) {
    case 'payment':
      return FaChartLine;
    case 'member':
      return FaUsers;
    case 'warning':
      return FaBell;
    case 'checkin':
    default:
      return FaClipboardCheck;
  }
};

const getInitials = value =>
  value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('') || 'PF';

const getGreeting = date => {
  const hours = date.getHours();
  if (hours < 12) {
    return 'Good morning';
  }
  if (hours < 17) {
    return 'Good afternoon';
  }
  if (hours < 21) {
    return 'Good evening';
  }

  return 'Good night';
};

const resolveDashboardContent = profile => {
  const displayLabel = profile?.displayLabel || 'Outlet';

  switch (profile?.type) {
    case 'restaurant':
      return {
        heroMessage: 'Pantau omzet cafe, bill aktif, dan ritme service outlet dari satu dashboard.',
        flowLabel: 'Tracked flow',
        queueMetricTitle: 'Open bills',
        queueMetricLabel: 'Open bills:',
        tableSectionTitle: 'Bill board',
        tableIdentityLabel: 'Bill',
        tableEmptyState: 'Belum ada bill aktif untuk outlet ini.',
        transactionPrefix: 'Bill',
        primaryPanelTitle: 'Cafe snapshot',
        primaryPanelSummaryLabel: 'Today cafe sales',
        secondaryChartTitle: 'Cafe revenue trend',
        planBadgeLabel: 'Cafe playbook',
        planDescription:
          'Gunakan analytics cafe untuk memantau penjualan menu, performa kasir, dan operasional outlet tanpa keluar dari dashboard ini.',
        analyticsActionLabel: 'Open cafe analytics',
        railTitle: 'Open bills',
        railDescription:
          'Pantau bill yang masih terbuka agar service floor dan kasir tetap sinkron sepanjang shift.',
        railEmptyState: 'No open bill is waiting in this outlet.',
      };
    case 'laundry':
      return {
        heroMessage: 'Pantau omzet laundry, order aktif, dan ritme operasional outlet dari satu dashboard.',
        flowLabel: 'Tracked flow',
        queueMetricTitle: 'Active orders',
        queueMetricLabel: 'Active orders:',
        tableSectionTitle: 'Laundry board',
        tableIdentityLabel: 'Order',
        tableEmptyState: 'Belum ada order laundry aktif untuk outlet ini.',
        transactionPrefix: 'Order',
        primaryPanelTitle: 'Laundry snapshot',
        primaryPanelSummaryLabel: 'Today laundry sales',
        secondaryChartTitle: 'Laundry revenue trend',
        planBadgeLabel: 'Laundry playbook',
        planDescription:
          'Gunakan analytics laundry untuk memantau order aktif, pelanggan, dan revenue outlet tanpa keluar dari dashboard ini.',
        analyticsActionLabel: 'Open laundry analytics',
        railTitle: 'Active orders',
        railDescription:
          'Pantau order laundry yang masih berjalan agar serah-terima dan pembayaran tetap rapi sepanjang shift.',
        railEmptyState: 'No active laundry order is waiting in this outlet.',
      };
    case 'pos_store':
      return {
        heroMessage: 'Pantau omzet store, transaksi aktif, dan ritme penjualan produk dari satu dashboard outlet.',
        flowLabel: 'Tracked flow',
        queueMetricTitle: 'Open carts',
        queueMetricLabel: 'Open carts:',
        tableSectionTitle: 'Store board',
        tableIdentityLabel: 'Cart',
        tableEmptyState: 'Belum ada transaksi store aktif untuk outlet ini.',
        transactionPrefix: 'Cart',
        primaryPanelTitle: 'Store snapshot',
        primaryPanelSummaryLabel: 'Today store sales',
        secondaryChartTitle: 'Store revenue trend',
        planBadgeLabel: 'Store playbook',
        planDescription:
          'Gunakan analytics store untuk memantau penjualan, stok, dan operasional outlet tanpa keluar dari dashboard ini.',
        analyticsActionLabel: 'Open store analytics',
        railTitle: 'Open carts',
        railDescription:
          'Pantau transaksi yang masih terbuka agar kasir dan stok outlet tetap sinkron sepanjang shift.',
        railEmptyState: 'No open cart is waiting in this outlet.',
      };
    case 'cafe_store':
      return {
        heroMessage:
          'Pantau omzet cafe + store, ticket aktif, dan ritme kasir outlet dari satu dashboard terpadu.',
        flowLabel: 'Tracked flow',
        queueMetricTitle: 'Open tickets',
        queueMetricLabel: 'Open tickets:',
        tableSectionTitle: 'Cafe + Store board',
        tableIdentityLabel: 'Ticket',
        tableEmptyState: 'Belum ada ticket aktif untuk outlet ini.',
        transactionPrefix: 'Ticket',
        primaryPanelTitle: 'Cafe + Store snapshot',
        primaryPanelSummaryLabel: 'Today cafe + store sales',
        secondaryChartTitle: 'Cafe + Store revenue trend',
        planBadgeLabel: 'Cafe + Store playbook',
        planDescription:
          'Gunakan analytics cafe + store untuk memantau penjualan, stok, dan ritme operasional outlet tanpa keluar dari dashboard ini.',
        analyticsActionLabel: 'Open cafe + store analytics',
        railTitle: 'Open tickets',
        railDescription:
          'Pantau ticket outlet yang masih aktif agar counter, kasir, dan stok tetap sinkron sepanjang shift.',
        railEmptyState: 'No active ticket is waiting in this outlet.',
      };
    case 'gym_store':
      return {
        heroMessage: 'Pantau traffic gym, membership, dan penjualan store outlet dari satu dashboard hybrid.',
        flowLabel: 'Gym + Store flow',
        primaryPanelTitle: 'Gym + Store overview',
        secondaryChartTitle: 'Gym + Store momentum',
      };
    case 'cafe_gym':
      return {
        heroMessage: 'Pantau traffic gym, layanan cafe, dan transaksi outlet dari satu dashboard hybrid.',
        flowLabel: 'Cafe + Gym flow',
        primaryPanelTitle: 'Cafe + Gym overview',
        secondaryChartTitle: 'Cafe + Gym momentum',
      };
    case 'hybrid':
      return {
        heroMessage: 'Pantau operasional gym sekaligus transaksi POS outlet dari satu dashboard hybrid.',
        flowLabel: 'Hybrid flow',
        primaryPanelTitle: 'Gym + POS overview',
        secondaryChartTitle: 'Outlet momentum',
      };
    default:
      if (profile?.dashboardMode === 'pos') {
        return {
          heroMessage: `Pantau omzet, transaksi aktif, dan ritme operasional ${displayLabel.toLowerCase()} dari satu dashboard outlet.`,
          flowLabel: 'Tracked flow',
          queueMetricTitle: 'Open transactions',
          queueMetricLabel: 'Open transactions:',
          tableSectionTitle: 'Transaction board',
          tableIdentityLabel: 'Transaction',
          tableEmptyState: 'Belum ada transaksi aktif untuk outlet ini.',
          transactionPrefix: 'Txn',
          primaryPanelTitle: 'Operational snapshot',
          primaryPanelSummaryLabel: 'Today sales',
          secondaryChartTitle: 'Revenue trend',
          planBadgeLabel: `${displayLabel} playbook`,
          planDescription: `Gunakan analytics ${displayLabel.toLowerCase()} untuk memantau penjualan, pelanggan, dan operasional outlet tanpa keluar dari dashboard ini.`,
          analyticsActionLabel: `Open ${displayLabel.toLowerCase()} analytics`,
          railTitle: 'Active transactions',
          railDescription:
            'Pantau transaksi outlet yang masih terbuka agar operasional kasir tetap rapi sepanjang shift.',
          railEmptyState: 'No active transaction is waiting in this outlet.',
        };
      }

      if (profile?.dashboardMode === 'hybrid') {
        return {
          heroMessage: `Pantau performa gym sekaligus penjualan POS ${displayLabel.toLowerCase()} tanpa pindah workspace.`,
          flowLabel: 'Hybrid flow',
          primaryPanelTitle: 'Gym + POS overview',
          secondaryChartTitle: 'Outlet momentum',
        };
      }

      return {
        heroMessage: 'Pantau member, check-in, dan performa outlet gym dari dashboard utama secara real-time.',
        flowLabel: 'Tracked flow',
        primaryPanelTitle: 'Sales Overview',
        secondaryChartTitle: 'Outlet momentum',
      };
  }
};

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState('all');
  const [outlets, setOutlets] = useState([]);
  const [summary, setSummary] = useState(createEmptySummary());
  const [posSummary, setPosSummary] = useState(createEmptyPOSSummary());
  const [memberPreview, setMemberPreview] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [liveNow, setLiveNow] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [posLoading, setPosLoading] = useState(false);
  const [membersLoading, setMembersLoading] = useState(true);
  const [outletLoading, setOutletLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  const currentUser = readStoredAdminUser();
  const currentUserOutletExperience = resolveOutletExperience(currentUser.outlet_type ?? currentUser.OutletType ?? '');
  const allowedPaths = getAllowedPaths(currentUser);
  const allowedPathSet = new Set(allowedPaths);

  const displayName =
    currentUser.full_name ||
    currentUser.name ||
    currentUser.username ||
    currentUser.AdminName ||
    'Admin';
  const displayRole = currentUser.role || currentUser.level || 'Administrator';
  const canReviewAllOutlets = currentUser.actor_type === 'admin';
  const isEmployeeExperience = currentUser.actor_type === 'karyawan';
  const canViewMembers = allowedPathSet.has('*') || allowedPathSet.has('/members');
  const canViewCheckinReport = allowedPathSet.has('*') || allowedPathSet.has('/reportcheckin');
  const canManageEmployeeModules =
    !isEmployeeExperience && (allowedPathSet.has('*') || allowedPathSet.has('/karyawanmodules'));
  const userInitials = getInitials(displayName);
  const greeting = getGreeting(liveNow);
  const liveTimeLabel = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(liveNow);
  const selectedOutletData =
    selectedOutlet === 'all'
      ? null
      : outlets.find(outlet => String(outlet.id) === String(selectedOutlet)) || null;
  const activeOutletProfile = resolveOutletExperience(
    selectedOutletData?.type || currentUserOutletExperience.type
  );
  const dashboardContent = resolveDashboardContent(activeOutletProfile);
  const isOutletScopedDashboard = Boolean(activeOutletProfile.type) && (!canReviewAllOutlets || selectedOutlet !== 'all');
  const isPosOnlyDashboard = isOutletScopedDashboard && activeOutletProfile.isPosOnly;
  const isHybridDashboard = isOutletScopedDashboard && activeOutletProfile.dashboardMode === 'hybrid';
  const supportsPOSDashboard = isOutletScopedDashboard && activeOutletProfile.supportsPos;

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setLiveNow(new Date());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!canReviewAllOutlets && currentUser.outlet_id) {
      setSelectedOutlet(String(currentUser.outlet_id));
    }
  }, [canReviewAllOutlets, currentUser.outlet_id]);

  useEffect(() => {
    if (!canReviewAllOutlets) {
      setOutlets(
        currentUser.outlet_id
          ? [
              {
                id: currentUser.outlet_id,
                name: currentUser.outlet_name || 'Assigned outlet',
                type: currentUserOutletExperience.type,
              },
            ]
          : []
      );
      setOutletLoading(false);
      return;
    }

    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }

    const fetchOutlets = async () => {
      setOutletLoading(true);

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outletlist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ limit: 100, offset: 0 }),
        });

        if (response.status === 401) {
          navigate('/loginadmin');
          return;
        }

        const data = await response.json();
        if (isSuccessResponse(data.error) && Array.isArray(data.results)) {
          setOutlets(
            data.results.map(outlet => ({
              id: outlet.ID || outlet.id,
              name: outlet.OutletName || outlet.outlet_name,
              type: resolveOutletExperience(outlet.OutletType ?? outlet.outlet_type ?? '').type,
            }))
          );
        } else {
          setOutlets([]);
        }
      } catch (fetchError) {
        console.error('Error fetching outlets:', fetchError);
        setOutlets([]);
      } finally {
        setOutletLoading(false);
      }
    };

    fetchOutlets();
  }, [canReviewAllOutlets, currentUser.outlet_id, currentUser.outlet_name, currentUserOutletExperience.type, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }

    const fetchDashboardSummary = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}dashboard/summary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet: selectedOutlet === 'all' ? 0 : Number(selectedOutlet),
            year: new Date().getFullYear(),
          }),
        });

        if (response.status === 401) {
          navigate('/loginadmin');
          return;
        }

        const data = await response.json();
        if (!response.ok || !isSuccessResponse(data.error)) {
          throw new Error(data.message || 'Gagal memuat dashboard');
        }

        setSummary(normalizeSummary(data.data));
      } catch (fetchError) {
        console.error('Error fetching dashboard summary:', fetchError);
        setSummary(createEmptySummary());
        setError(fetchError.message || 'Gagal memuat dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardSummary();
  }, [navigate, refreshTick, selectedOutlet]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }

    if (!supportsPOSDashboard || selectedOutlet === 'all' || !selectedOutlet || !POS_API_BASE_URL) {
      setPosSummary(createEmptyPOSSummary());
      setPosLoading(false);
      return;
    }

    let isMounted = true;

    const fetchPOSSummary = async () => {
      setPosLoading(true);

      try {
        const [dashboardResponse, revenueResponse] = await Promise.all([
          fetch(`${POS_API_BASE_URL}api/transactions/dashboarddata?outlet=${encodeURIComponent(selectedOutlet)}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
          fetch(`${POS_API_BASE_URL}api/balance/revenue?outlet=${encodeURIComponent(selectedOutlet)}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        if (dashboardResponse.status === 401 || revenueResponse.status === 401) {
          navigate('/loginadmin');
          return;
        }

        const dashboardData = await parseDashboardJsonResponse(dashboardResponse);
        const revenueData = await parseDashboardJsonResponse(revenueResponse);

        if (!dashboardResponse.ok && !revenueResponse.ok) {
          throw new Error(
            revenueData?.error ||
            revenueData?.message ||
            dashboardData?.error ||
            dashboardData?.message ||
            'Gagal memuat ringkasan POS outlet'
          );
        }

        if (isMounted) {
          setPosSummary(normalizePOSSummary({
            ...(dashboardResponse.ok ? dashboardData : {}),
            ...(revenueResponse.ok ? revenueData : {}),
          }));
        }
      } catch (fetchError) {
        console.error('Error fetching POS dashboard summary:', fetchError);
        if (isMounted) {
          setPosSummary(createEmptyPOSSummary());
        }
      } finally {
        if (isMounted) {
          setPosLoading(false);
        }
      }
    };

    fetchPOSSummary();
    return () => {
      isMounted = false;
    };
  }, [navigate, refreshTick, selectedOutlet, supportsPOSDashboard]);

  useEffect(() => {
    if (!canViewMembers) {
      setMemberPreview([]);
      setMembersLoading(false);
      return;
    }

    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }

    const fetchMembers = async () => {
      setMembersLoading(true);

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}member/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            limit: 4,
            offset: 0,
            outlet: selectedOutlet === 'all' ? 0 : Number(selectedOutlet),
          }),
        });

        if (response.status === 401) {
          navigate('/loginadmin');
          return;
        }

        const data = await response.json();
        if (isSuccessResponse(data.error) && Array.isArray(data.results)) {
          setMemberPreview(data.results.slice(0, 4));
        } else {
          setMemberPreview([]);
        }
      } catch (fetchError) {
        console.error('Error fetching member preview:', fetchError);
        setMemberPreview([]);
      } finally {
        setMembersLoading(false);
      }
    };

    fetchMembers();
  }, [canViewMembers, navigate, refreshTick, selectedOutlet]);

  const approvedMemberships = summary.sales_summary.reduce(
    (total, item) => total + Number(item.approved_memberships || 0),
    0
  );
  const pendingMemberships = summary.sales_summary.reduce(
    (total, item) => total + Number(item.pending_memberships || 0),
    0
  );
  const ptPackages = summary.sales_summary.reduce((total, item) => total + Number(item.pt_packages || 0), 0);
  const openPOSTransactions = Array.isArray(posSummary.transactions) ? posSummary.transactions : [];
  const posQueueCount = openPOSTransactions.length;
  const posTodaySales = Number(posSummary.today_sales || posSummary.today_revenue || 0);
  const posYesterdayRevenue = Number(posSummary.yesterday_revenue || 0);
  const posWeeklyRevenue = Number(posSummary.weekly_revenue || 0);
  const posMonthlyRevenue = Number(posSummary.monthly_revenue || 0);
  const posTotalSales = Number(posSummary.total_sales || 0);
  const posRevenueTrendDirection = posTodaySales >= posYesterdayRevenue;
  const sharedDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#0c1015',
        titleColor: '#f4f7f4',
        bodyColor: '#c0c8d0',
        borderColor: 'rgba(188, 255, 77, 0.24)',
        borderWidth: 1,
      },
    },
    cutout: '68%',
  };
  const salesBreakdown = isHybridDashboard
    ? [
        { label: 'Attendance', value: summary.stats.today_checkins, color: '#eef8dc' },
        { label: 'Membership', value: approvedMemberships, color: '#bcff4d' },
        { label: 'PT Sessions', value: ptPackages, color: '#5fa61d' },
        { label: 'Open POS', value: posQueueCount, color: '#234212' },
      ]
    : [
        { label: 'Memberships', value: approvedMemberships, color: '#eef8dc' },
        { label: 'Pending', value: pendingMemberships, color: '#bcff4d' },
        { label: 'PT Sessions', value: ptPackages, color: '#5fa61d' },
        { label: 'Attendance', value: summary.stats.today_checkins, color: '#234212' },
      ];
  const totalTrackedEvents = salesBreakdown.reduce((total, item) => total + Number(item.value || 0), 0);
  const doughnutDatasetValues =
    totalTrackedEvents > 0 ? salesBreakdown.map(item => Number(item.value || 0)) : [1, 1, 1, 1];
  const doughnutData = {
    labels: salesBreakdown.map(item => item.label),
    datasets: [
      {
        data: doughnutDatasetValues,
        backgroundColor: salesBreakdown.map(item => item.color),
        borderColor: '#171c22',
        borderWidth: 4,
        hoverOffset: 2,
      },
    ],
  };
  const trendChartData = {
    labels: summary.activity_summary.map(item => item.label),
    datasets: [
      {
        data: summary.activity_summary.map(
          item =>
            Number(item.checkins || 0) * 10 +
            Number(item.new_members || 0) * 18 +
            Number(item.pt_sessions || 0) * 14
        ),
        borderColor: '#20f29f',
        borderWidth: 2.4,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.42,
        fill: true,
        backgroundColor: context => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return 'rgba(32, 242, 159, 0.18)';
          }

          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(32, 242, 159, 0.32)');
          gradient.addColorStop(1, 'rgba(32, 242, 159, 0)');
          return gradient;
        },
      },
    ],
  };
  const sharedTrendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#0c1015',
        titleColor: '#f4f7f4',
        bodyColor: '#c0c8d0',
        borderColor: 'rgba(188, 255, 77, 0.24)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
  };
  const posTrendChartData = {
    labels: ['Kemarin', 'Hari ini', 'Minggu ini', 'Bulan ini'],
    datasets: [
      {
        data: [posYesterdayRevenue, posTodaySales, posWeeklyRevenue, posMonthlyRevenue],
        borderColor: '#20f29f',
        borderWidth: 2.4,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.36,
        fill: true,
        backgroundColor: context => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return 'rgba(32, 242, 159, 0.18)';
          }

          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(32, 242, 159, 0.32)');
          gradient.addColorStop(1, 'rgba(32, 242, 159, 0)');
          return gradient;
        },
      },
    ],
  };
  const notificationItems = summary.notifications;
  const activityItems = summary.recent_activity;
  const managerContacts = summary.manager_contacts;
  const accessibleNavigation = filterAdminNavigationSections(adminNavigationSections, currentUser, '');
  const filteredNavigation = filterAdminNavigationSections(adminNavigationSections, currentUser, searchQuery);
  const outletLabel = selectedOutletData ? selectedOutletData.name : currentUser.outlet_name || 'All outlets';
  const dashboardGeneratedLabel = summary.meta?.generated_at ? formatDateTime(summary.meta.generated_at) : 'Live data';
  const customerRows = memberPreview.map(member => ({
    id: member.id,
    name: `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Member',
    contact: member.email || member.hp || 'No contact saved',
    outlet:
      outlets.find(outlet => String(outlet.id) === String(member.outlet))?.name || 'Outlet not linked',
  }));
  const featuredPlan = summary.featured_plan;
  const featuredPlanDescription = featuredPlan
    ? `${formatNumber(featuredPlan.total_sales)} sales recorded${featuredPlan.outlet_name ? ` in ${featuredPlan.outlet_name}` : ''}.`
    : 'No membership package has been created for this outlet yet.';
  const actorPaymentSummary = summary.actor_payment_summary || createEmptySummary().actor_payment_summary;
  const actorMemberSummary = summary.actor_member_summary || createEmptySummary().actor_member_summary;
  const todayBirthdayCount = Array.isArray(summary.birthdays?.today) ? summary.birthdays.today.length : 0;
  const tomorrowBirthdayCount = Array.isArray(summary.birthdays?.tomorrow) ? summary.birthdays.tomorrow.length : 0;
  const adminHeroPills = [
    {
      label: canReviewAllOutlets ? 'Outlet scope' : 'Current outlet',
      value:
        canReviewAllOutlets && selectedOutlet === 'all'
          ? `${formatNumber(summary.stats.total_outlets)} outlets connected`
          : outletLabel,
    },
    ...(isOutletScopedDashboard
      ? [
          {
            label: 'Outlet model',
            value: activeOutletProfile.displayLabel,
          },
        ]
      : []),
    {
      label: 'Last synced',
      value: dashboardGeneratedLabel,
    },
  ];
  const adminOverviewCards = isPosOnlyDashboard
    ? [
        {
          title: 'Sales today',
          value: formatCompactCurrency(posTodaySales),
          note: `${activeOutletProfile.displayLabel} sales yang tercatat hari ini`,
          icon: FaChartLine,
          accent: 'revenue',
        },
        {
          title: 'Revenue this week',
          value: formatCompactCurrency(posWeeklyRevenue),
          note: 'Akumulasi revenue mingguan outlet aktif',
          icon: FaClipboardCheck,
          accent: 'attendance',
        },
        {
          title: 'Revenue this month',
          value: formatCompactCurrency(posMonthlyRevenue),
          note: 'Revenue bulanan yang sudah diposting',
          icon: FaBell,
          accent: 'birthday',
        },
        {
          title: dashboardContent.queueMetricTitle,
          value: formatNumber(posQueueCount),
          note: `${formatCompactCurrency(posTotalSales)} total sales recorded`,
          icon: FaUsers,
          accent: 'members',
        },
      ]
    : isHybridDashboard
      ? [
          {
            title: 'Gym attendance',
            value: formatNumber(summary.stats.today_checkins),
            note: 'Traffic member dan kunjungan hari ini',
            icon: FaWalking,
            accent: 'attendance',
          },
          {
            title: 'Membership revenue',
            value: formatCompactCurrency(summary.stats.today_revenue),
            note: `${formatNumber(summary.stats.active_members)} active memberships are currently running`,
            icon: FaUsers,
            accent: 'members',
          },
          {
            title: 'POS sales today',
            value: formatCompactCurrency(posTodaySales),
            note: `${formatNumber(posQueueCount)} transaksi POS masih terbuka`,
            icon: FaChartLine,
            accent: 'revenue',
          },
          {
            title: 'Birthdays & follow-up',
            value: formatNumber(todayBirthdayCount + tomorrowBirthdayCount),
            note: `${formatNumber(todayBirthdayCount)} today • ${formatNumber(tomorrowBirthdayCount)} tomorrow`,
            icon: FaBell,
            accent: 'birthday',
          },
        ]
      : [
    {
      title: 'Today attendance',
      value: formatNumber(summary.stats.today_checkins),
      note: 'Total kehadiran member yang tercatat hari ini, termasuk member harian',
      icon: FaWalking,
      accent: 'attendance',
    },
    {
      title: 'Members added today',
      value: formatNumber(summary.stats.today_members_added),
      note: `${formatNumber(summary.stats.active_members)} active memberships are currently running`,
      icon: FaUsers,
      accent: 'members',
    },
    {
      title: 'Revenue today',
      value: formatCompactCurrency(summary.stats.today_revenue),
      note: 'Approved membership payments recorded today',
      icon: FaChartLine,
      accent: 'revenue',
    },
    {
      title: 'Birthdays due',
      value: formatNumber(todayBirthdayCount + tomorrowBirthdayCount),
      note: `${formatNumber(todayBirthdayCount)} today • ${formatNumber(tomorrowBirthdayCount)} tomorrow`,
      icon: FaBell,
      accent: 'birthday',
    },
  ];
  const quickStatPanels = isPosOnlyDashboard
    ? [
        {
          label: dashboardContent.queueMetricLabel,
          value: formatNumber(posQueueCount),
          caption: `${formatCompactCurrency(posTodaySales)} sales today`,
          icon: FaClipboardCheck,
        },
        {
          label: 'Total sales:',
          value: formatCompactCurrency(posTotalSales),
          caption: `${formatCompactCurrency(posMonthlyRevenue)} this month`,
          icon: FaChartLine,
        },
      ]
    : isHybridDashboard
      ? [
          {
            label: 'Active members:',
            value: formatNumber(summary.stats.active_members),
            caption: `${formatNumber(summary.stats.total_members)} total registered`,
            icon: FaUsers,
          },
          {
            label: 'Open POS transactions:',
            value: formatNumber(posQueueCount),
            caption: `${formatCompactCurrency(posMonthlyRevenue)} POS revenue this month`,
            icon: FaClipboardCheck,
          },
        ]
      : [
    {
      label: 'Active members:',
      value: formatNumber(summary.stats.active_members),
      caption: `${formatNumber(summary.stats.total_members)} total registered`,
      icon: FaUsers,
    },
    {
      label: 'Pending payments:',
      value: formatNumber(summary.stats.pending_payments),
      caption: 'Awaiting approval',
      icon: FaClipboardCheck,
    },
  ];
  const outletDashboardMessage = dashboardContent.heroMessage;
  const tableSectionTitle = isPosOnlyDashboard ? dashboardContent.tableSectionTitle : 'Customer list';
  const tableColumnLabels = isPosOnlyDashboard
    ? [dashboardContent.tableIdentityLabel, 'Payment', 'Total']
    : ['Name', 'Outlet', 'Contact'];
  const transactionRows = openPOSTransactions.slice(0, 4).map(row => ({
    id: row.id,
    name: `${dashboardContent.transactionPrefix} #${row.id || '-'}`,
    sublabel: row.cashier_name || 'Cashier belum terisi',
    middle: row.payment_method || 'Payment pending',
    right: formatCurrency(row.total || 0),
    note: row.created_at ? formatDateTime(row.created_at) : '',
  }));
  const primaryPanelTitle = dashboardContent.primaryPanelTitle;
  const primaryPanelSummaryLabel = isPosOnlyDashboard
    ? dashboardContent.primaryPanelSummaryLabel
    : isHybridDashboard
      ? 'Combined monthly revenue'
      : 'Month to date revenue';
  const primaryPanelSummaryValue = isPosOnlyDashboard
    ? formatCompactCurrency(posTodaySales)
    : isHybridDashboard
      ? formatCompactCurrency(summary.stats.monthly_revenue + posMonthlyRevenue)
      : formatCompactCurrency(summary.stats.monthly_revenue);
  const primaryPanelStats = isPosOnlyDashboard
    ? [
        { label: 'Today sales', value: formatCompactCurrency(posTodaySales) },
        { label: 'Yesterday', value: formatCompactCurrency(posYesterdayRevenue) },
        { label: 'This week', value: formatCompactCurrency(posWeeklyRevenue) },
        { label: dashboardContent.queueMetricTitle, value: formatNumber(posQueueCount) },
      ]
    : salesBreakdown.map(item => ({
        label: item.label,
        value: formatCompactNumber(item.value),
        color: item.color,
      }));
  const secondaryChartTitle = dashboardContent.secondaryChartTitle;
  const secondaryChartValue = isPosOnlyDashboard ? formatCompactCurrency(posMonthlyRevenue) : formatNumber(summary.stats.today_checkins);
  const secondaryChartCaption = isPosOnlyDashboard
    ? `${posRevenueTrendDirection ? 'Naik' : 'Turun'} dibanding kemarin`
    : 'Attendance recorded today';
  const posRailLink = selectedOutlet && selectedOutlet !== 'all'
    ? `/report-center?tab=pos&outlet_id=${encodeURIComponent(selectedOutlet)}`
    : '/report-center?tab=pos';
  const birthdayHighlights = [
    ...(Array.isArray(summary.birthdays?.today) ? summary.birthdays.today.map(item => ({ ...item, badge: 'Today' })) : []),
    ...(Array.isArray(summary.birthdays?.tomorrow) ? summary.birthdays.tomorrow.map(item => ({ ...item, badge: 'Tomorrow' })) : []),
    ...(Array.isArray(summary.birthdays?.upcoming) ? summary.birthdays.upcoming.map(item => ({ ...item, badge: `${item.days_away} days` })) : []),
  ].slice(0, 5);
  const employeeModuleItems = accessibleNavigation
    .flatMap(section => section.items)
    .filter(item => item.to !== '/dashboardadmin');
  const hasPaymentWorkspace = allowedPathSet.has('*') || allowedPathSet.has('/membership-payment');
  const employeeModuleCards = employeeModuleItems.map(item => {
    let value = 'Enabled';
    let note = 'This module is ready to use from your assigned workspace.';
    let meta = item.label;

    switch (item.to) {
      case '/membership-payment':
        value = formatNumber(actorPaymentSummary.today_transactions);
        note = `${formatCurrency(actorPaymentSummary.today_revenue)} approved today`;
        meta = 'Transactions handled by this account';
        break;
      case '/members':
        value = isEmployeeExperience
          ? formatNumber(actorMemberSummary.today_added)
          : formatNumber(summary.stats.active_members);
        note = isEmployeeExperience
          ? `${formatNumber(actorMemberSummary.today_added)} members added today by this account`
          : `${formatNumber(summary.stats.total_members)} total members in your outlet`;
        meta = isEmployeeExperience ? 'Members registered by this account' : 'Active member base';
        break;
      case '/reportcheckin':
        value = formatNumber(summary.stats.today_checkins);
        note = 'Attendance recorded today';
        meta = 'Attendance monitoring';
        break;
      case '/membership-setting':
        value = formatNumber(actorPaymentSummary.today_completed);
        note = `${formatNumber(actorPaymentSummary.pending_transactions)} payments waiting review`;
        meta = 'Membership pipeline';
        break;
      case '/membership-daily-stats':
      case '/membership-monthly-stats':
        value = formatCurrency(actorPaymentSummary.today_revenue);
        note = `${formatNumber(actorPaymentSummary.today_completed)} approvals completed today`;
        meta = 'Performance statistics';
        break;
      case '/pt-schedule-setting':
        value = formatNumber(summary.stats.today_checkins);
        note = 'Monthly PT calendar for trainer sessions';
        meta = 'Schedule calendar';
        break;
      default:
        if (item.to === '/deposits' || item.to === '/user-balance') {
          value = formatCurrency(actorPaymentSummary.today_revenue);
          note = `${formatNumber(actorPaymentSummary.pending_transactions)} payments pending review`;
          meta = 'Finance visibility';
        }
        break;
    }

    return {
      ...item,
      value,
      note,
      meta,
    };
  });
  const recentEmployeeTransactions = Array.isArray(actorPaymentSummary.recent_transactions)
    ? actorPaymentSummary.recent_transactions.slice(0, 4)
    : [];
  const recentEmployeeMembers = Array.isArray(actorMemberSummary.recent_members)
    ? actorMemberSummary.recent_members.slice(0, 4)
    : [];
  const employeeOverviewCards = hasPaymentWorkspace
    ? [
        {
          label: 'Transactions today',
          value: formatNumber(actorPaymentSummary.today_transactions),
          note: 'Payments created by this account today',
        },
        {
          label: 'Completed today',
          value: formatNumber(actorPaymentSummary.today_completed),
          note: `${formatNumber(actorPaymentSummary.pending_transactions)} payments still in queue`,
        },
        {
          label: 'Revenue today',
          value: formatCurrency(actorPaymentSummary.today_revenue),
          note: 'Approved revenue recorded today',
        },
      ]
    : [
        {
          label: 'Today attendance',
          value: formatNumber(summary.stats.today_checkins),
          note: 'Current traffic in your outlet',
        },
        {
          label: 'Members added today',
          value: formatNumber(actorMemberSummary.today_added),
          note: 'Registrations created by this account today',
        },
        {
          label: 'Birthdays today',
          value: formatNumber(todayBirthdayCount),
          note: `${formatNumber(tomorrowBirthdayCount)} more tomorrow`,
        },
      ];
  const employeeActivityEyebrow = hasPaymentWorkspace ? 'Activity today' : 'Operational updates';
  const employeeActivityTitle = hasPaymentWorkspace ? 'Recent payments' : 'Latest outlet updates';
  const employeeActivityDescription = hasPaymentWorkspace
    ? 'Track membership payments handled by this account without opening another report first.'
    : 'Review the latest operational signals from your assigned outlet in one place.';
  const employeeActivityMeta = hasPaymentWorkspace
    ? `${formatNumber(recentEmployeeTransactions.length)} recent records`
    : `${formatNumber(activityItems.length)} current items`;
  const employeeModuleDescription = hasPaymentWorkspace
    ? 'Open the modules you use most and continue processing from the same workspace.'
    : 'Use these shortcuts to move between the modules assigned to your account.';
  const employeeReminderStats = [
    {
      label: 'Today',
      value: formatNumber(todayBirthdayCount),
    },
    {
      label: 'Tomorrow',
      value: formatNumber(tomorrowBirthdayCount),
    },
    {
      label: 'Added today',
      value: formatNumber(actorMemberSummary.today_added),
    },
  ];
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isSidebarOpen) {
      document.body.style.overflow = '';
      return undefined;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  return (
    <div className={`reference-dashboard ${isSidebarOpen ? 'is-menu-open' : ''}`}>
        <button
          type="button"
          className={`reference-mobile-launcher ${isSidebarOpen ? 'is-hidden' : ''}`}
          aria-label="Open menu"
          onClick={() => setIsSidebarOpen(true)}
        >
          <FaBars />
          <span>Menu</span>
        </button>

        <div className="reference-shell">
          <button
            type="button"
            className={`reference-mobile-backdrop ${isSidebarOpen ? 'is-open' : ''}`}
            aria-label="Close menu"
            onClick={() => setIsSidebarOpen(false)}
          />

          <aside className={`reference-sidebar ${isSidebarOpen ? 'is-open' : ''}`}>
            <div className="reference-sidebar__mobile-head">
              <div className="reference-sidebar__mobile-brand">
                <FaDumbbell />
                <span>Primefit Menu</span>
              </div>
              <button
                type="button"
                className="reference-sidebar__mobile-close"
                aria-label="Close menu"
                onClick={() => setIsSidebarOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="reference-sidebar__user">
              <span className="reference-sidebar__avatar">{userInitials}</span>
              <div>
                <strong>{displayName}</strong>
                <small>{displayRole}</small>
              </div>
            </div>

            <div className="reference-sidebar__search">
              <FaSearch />
              <input
                type="search"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                placeholder="Search..."
                aria-label="Search navigation"
              />
              <span>Ctrl K</span>
            </div>

            <div className="reference-sidebar__sections">
              {filteredNavigation.map(section => (
                <div key={section.title} className="reference-sidebar__section">
                  <p>{section.title}</p>

                  <div className="reference-sidebar__menu">
                    {section.items.map(item => {
                      const Icon = item.icon;
                      const isActive =
                        location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          className={`reference-sidebar__item ${isActive ? 'is-active' : ''}`}
                          onClick={() => setIsSidebarOpen(false)}
                        >
                          <Icon />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="reference-sidebar__footer">
              <button
                type="button"
                className="reference-sidebar__utility"
                onClick={toggleTheme}
              >
                {isDark ? <FaSun /> : <FaMoon />}
                <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
              </button>

              <Link
                to="/logout"
                className="reference-sidebar__utility reference-sidebar__utility--danger"
                onClick={() => setIsSidebarOpen(false)}
              >
                <FaSignOutAlt />
                <span>Logout</span>
              </Link>
            </div>

            <div className="reference-sidebar__brand">
              <FaDumbbell />
              <span>PRIMEFIT</span>
            </div>
          </aside>

          <main className="reference-main">
            <div className="reference-mobile-header">
              <div className="reference-mobile-header__title">
                <strong>{greeting}, {displayName}</strong>
                <small>{liveTimeLabel}</small>
              </div>

              <div className="reference-mobile-header__actions">
                <button
                  type="button"
                  className="reference-icon-button"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  onClick={toggleTheme}
                >
                  {isDark ? <FaSun /> : <FaMoon />}
                </button>
                <button
                  type="button"
                  className="reference-icon-button"
                  aria-label="Refresh dashboard"
                  onClick={() => setRefreshTick(current => current + 1)}
                >
                  <FaRedoAlt />
                </button>
                <button type="button" className="reference-icon-button" aria-label="Notifications">
                  <FaBell />
                </button>
              </div>
            </div>

            <header className="reference-topbar">
              <div className="reference-topbar__crumbs">
                <FaLayerGroup />
                <FaStar />
                <span>
                  Dashboards <FaChevronRight /> Overview
                </span>
              </div>

              <div className="reference-topbar__actions">
                <button
                  type="button"
                  className="reference-icon-button"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  onClick={toggleTheme}
                >
                  {isDark ? <FaSun /> : <FaMoon />}
                </button>
                <button
                  type="button"
                  className="reference-icon-button"
                  aria-label="Refresh dashboard"
                  onClick={() => setRefreshTick(current => current + 1)}
                >
                  <FaRedoAlt />
                </button>
                <button type="button" className="reference-icon-button" aria-label="Notifications">
                  <FaBell />
                </button>
                <button type="button" className="reference-icon-button" aria-label="Region">
                  <FaGlobe />
                </button>
              </div>
            </header>

          <section className="reference-content">
            <section className="reference-hero-card">
              <div className={`reference-content__header ${isEmployeeExperience ? '' : 'reference-content__header--solo'}`}>
                <div className="reference-content__intro">
                  <p className="reference-content__eyebrow">{greeting}</p>
                  <h1>{isEmployeeExperience ? `Welcome, ${displayName}` : `Welcome back, ${displayName}`}</h1>
                  <p className="reference-content__message">
                    {isEmployeeExperience
                      ? 'Review the modules assigned to your account, monitor your current workload, and keep outlet operations on schedule.'
                      : outletDashboardMessage}
                  </p>
                  <small>{liveTimeLabel} • {outletLabel}</small>
                </div>

                {isEmployeeExperience ? (
                  <div className="reference-hero-card__side">
                    <div className="reference-role-card">
                      <strong>{displayRole}</strong>
                      <small>{outletLabel}</small>
                    </div>

                    <div className="reference-hero-card__status">
                      <span>Personal workspace</span>
                      <strong>{dashboardGeneratedLabel}</strong>
                    </div>
                  </div>
                ) : null}
              </div>
            </section>

            {!isEmployeeExperience ? (
              <div className="reference-hero-toolbar">
                <div className="reference-select reference-select--compact">
                  <Form.Select
                    value={selectedOutlet}
                    onChange={event => setSelectedOutlet(event.target.value)}
                    disabled={loading || outletLoading}
                    aria-label="Filter outlet"
                  >
                    {canReviewAllOutlets ? <option value="all">All outlets</option> : null}
                    {outlets.map(outlet => (
                      <option key={outlet.id} value={outlet.id}>
                        {outlet.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                <div className="reference-hero-pill-group">
                  {adminHeroPills.map(item => (
                    <div key={item.label} className="reference-hero-pill">
                      <small>{item.label}</small>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {error ? <Alert className="reference-alert">{error}</Alert> : null}

            {isEmployeeExperience ? (
              <>
                <div className="reference-employee-layout">
                  <article className="reference-panel reference-panel--employee-overview">
                    <div className="reference-panel__header reference-panel__header--stacked">
                      <div>
                        <span className="reference-panel__eyebrow">Work summary</span>
                        <h2>{hasPaymentWorkspace ? 'Today overview' : 'Daily outlet overview'}</h2>
                        <p className="reference-panel__description">
                          {hasPaymentWorkspace
                            ? 'Semua ringkasan utama di bawah ini dihitung dari aktivitas hari ini.'
                            : 'Ringkasan utama di bawah ini difokuskan pada aktivitas outlet hari ini.'}
                        </p>
                      </div>
                      <small className="reference-panel__meta">
                        {summary.meta?.generated_at ? formatDateTime(summary.meta.generated_at) : 'Live data'}
                      </small>
                    </div>

                    <div className="reference-employee-overview-grid">
                      {employeeOverviewCards.map(item => (
                        <article key={item.label} className="reference-employee-overview-card">
                          <small>{item.label}</small>
                          <strong>{item.value}</strong>
                          <span>{item.note}</span>
                        </article>
                      ))}
                    </div>
                  </article>

                  <div className="reference-employee-body">
                    <div className="reference-employee-main">
                      <article className="reference-panel reference-panel--employee-modules">
                        <div className="reference-panel__header reference-panel__header--stacked">
                          <div>
                            <span className="reference-panel__eyebrow">Quick access</span>
                            <h2>Assigned modules</h2>
                            <p className="reference-panel__description">{employeeModuleDescription}</p>
                          </div>
                          <small className="reference-panel__meta">
                            {formatNumber(employeeModuleCards.length)} active module{employeeModuleCards.length === 1 ? '' : 's'}
                          </small>
                        </div>

                        {employeeModuleCards.length === 0 ? (
                          <div className="reference-empty-table">No active module has been assigned to this account.</div>
                        ) : (
                          <div className="reference-employee-shortcut-list">
                            {employeeModuleCards.map(card => {
                              const Icon = card.icon;

                              return (
                                <Link key={card.to} to={card.to} className="reference-employee-shortcut">
                                  <span className="reference-employee-shortcut__icon">
                                    <Icon />
                                  </span>
                                  <div className="reference-employee-shortcut__copy">
                                    <small>{card.meta}</small>
                                    <strong>{card.label}</strong>
                                    <span>{card.note}</span>
                                  </div>
                                  <div className="reference-employee-shortcut__meta">
                                    <span>{card.value}</span>
                                    <FaChevronRight />
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </article>

                      <article className="reference-panel reference-panel--employee-focus">
                        <div className="reference-panel__header reference-panel__header--stacked">
                          <div>
                            <span className="reference-panel__eyebrow">{employeeActivityEyebrow}</span>
                            <h2>{employeeActivityTitle}</h2>
                            <p className="reference-panel__description">{employeeActivityDescription}</p>
                          </div>
                          <small className="reference-panel__meta">{employeeActivityMeta}</small>
                        </div>

                        {hasPaymentWorkspace ? (
                          <div className="reference-employee-transaction-list">
                            {recentEmployeeTransactions.length > 0 ? (
                              recentEmployeeTransactions.map(item => (
                                <article key={item.id} className="reference-employee-transaction">
                                  <div>
                                    <strong>{item.member_name}</strong>
                                    <small>{item.membership_name} • {item.payment_method || 'Method pending'}</small>
                                  </div>
                                  <div className="reference-employee-transaction__meta">
                                    <span>{formatCurrency(item.amount)}</span>
                                    <small>{formatDateTime(item.created_at)}</small>
                                    <em className={`is-${formatPaymentStatusLabel(item.status).toLowerCase()}`}>
                                      {formatPaymentStatusLabel(item.status)}
                                    </em>
                                  </div>
                                </article>
                              ))
                            ) : (
                              <div className="reference-empty-table">No membership payment has been recorded by this account yet.</div>
                            )}
                          </div>
                        ) : (
                          <div className="reference-employee-update-list">
                            {activityItems.length === 0 ? (
                              <div className="reference-empty-table">No operational update has been recorded for this outlet yet.</div>
                            ) : (
                              activityItems.map(item => (
                                <article key={`${item.kind}-${item.occurred_at}-${item.title}`} className="reference-activity-item">
                                  <span
                                    className="reference-activity-item__dot"
                                    style={{ backgroundColor: getActivityAccent(item.kind) }}
                                  />
                                  <div>
                                    <strong>{item.title}</strong>
                                    <small>{item.detail}</small>
                                    <small>
                                      {formatRelativeTime(item.occurred_at, {
                                        preferLocalWallClock: shouldPreferLocalWallClockForActivity(item.kind),
                                      })}
                                    </small>
                                  </div>
                                </article>
                              ))
                            )}
                          </div>
                        )}

                        {canViewMembers ? (
                          <div className="reference-employee-subsection">
                            <div className="reference-employee-section__head">
                              <h3>Members added by you</h3>
                              <span>{formatNumber(actorMemberSummary.today_added)} added today</span>
                            </div>

                            <div className="reference-employee-member-list">
                              {recentEmployeeMembers.length > 0 ? (
                                recentEmployeeMembers.map(item => (
                                  <article key={item.id} className="reference-employee-member-item">
                                    <div>
                                      <strong>{item.name}</strong>
                                      <small>{item.outlet_name}</small>
                                    </div>
                                    <small>{formatDateTime(item.created_at)}</small>
                                  </article>
                                ))
                              ) : (
                                <div className="reference-empty-table">No member registration has been recorded by this account yet.</div>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </article>
                    </div>

                    <article className="reference-panel reference-panel--employee-birthdays">
                      <div className="reference-panel__header reference-panel__header--stacked">
                        <div>
                          <span className="reference-panel__eyebrow">Member reminders</span>
                          <h2>Birthday watchlist</h2>
                          <p className="reference-panel__description">
                            Follow the nearest birthday reminders so the front desk can keep member engagement warm.
                          </p>
                        </div>
                        <small className="reference-panel__meta">
                          {formatNumber(birthdayHighlights.length)} scheduled reminders
                        </small>
                      </div>

                      <div className="reference-employee-reminder-summary">
                        {employeeReminderStats.map(item => (
                          <article key={item.label} className="reference-employee-reminder-chip">
                            <small>{item.label}</small>
                            <strong>{item.value}</strong>
                          </article>
                        ))}
                      </div>

                      <div className="reference-birthday-list">
                        {birthdayHighlights.length > 0 ? (
                          birthdayHighlights.map(item => (
                            <article key={`${item.id}-${item.next_birthday}`} className="reference-birthday-item">
                              <div className="reference-birthday-item__identity">
                                <span className="reference-birthday-item__avatar">{getInitials(item.name)}</span>
                                <div>
                                  <strong>{item.name}</strong>
                                  <small>{item.outlet_name || outletLabel}</small>
                                </div>
                              </div>
                              <div className="reference-birthday-item__meta">
                                <span>{item.badge}</span>
                                <small>{item.next_birthday}</small>
                              </div>
                            </article>
                          ))
                        ) : (
                          <div className="reference-empty-table">No birthday reminder is available for the selected outlet.</div>
                        )}
                      </div>
                    </article>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="reference-admin-overview">
                  {adminOverviewCards.map(metric => {
                    const Icon = metric.icon;

                    return (
                      <article
                        key={metric.title}
                        className={`reference-metric-card reference-metric-card--modern is-${metric.accent}`}
                      >
                        <div className="reference-metric-card__header">
                          <span className="reference-metric-card__icon">
                            <Icon />
                          </span>
                          <p>{metric.title}</p>
                        </div>

                        <div className="reference-metric-card__body">
                          <div>
                            <strong>{metric.value}</strong>
                            <small>{metric.note}</small>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
                <div className="reference-board">
                  <div className="reference-board__left">
                    <article className="reference-panel reference-panel--sales">
                      <div className="reference-panel__header">
                        <h2>{primaryPanelTitle}</h2>
                        <button type="button" aria-label="More options">
                          <FaEllipsisH />
                        </button>
                      </div>

                      <div className="reference-sales">
                        {!isPosOnlyDashboard ? (
                          <div className="reference-sales__chart">
                            <div className="reference-sales__chart-frame">
                              {loading || (isHybridDashboard && posLoading) ? (
                                <div className="reference-loading">
                                  <Spinner animation="border" size="sm" />
                                </div>
                              ) : (
                                <>
                                  <Doughnut data={doughnutData} options={sharedDoughnutOptions} />
                                  <div className="reference-sales__total">
                                    <strong>{formatCompactNumber(totalTrackedEvents)}</strong>
                                    <span>{dashboardContent.flowLabel}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ) : null}

                        <div className="reference-sales__legend">
                          <div className="reference-sales__legend-head">
                            <span>{primaryPanelSummaryLabel}</span>
                            <strong>{primaryPanelSummaryValue}</strong>
                          </div>

                          <div className="reference-sales__legend-grid">
                            {primaryPanelStats.map(item => (
                              <div key={item.label} className="reference-sales__legend-item">
                                {item.color ? (
                                  <span
                                    className="reference-sales__legend-dot"
                                    style={{ backgroundColor: item.color }}
                                  />
                                ) : null}
                                <div>
                                  <small>{item.label}</small>
                                  <strong>{item.value}</strong>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </article>

                    <article className="reference-panel reference-panel--table">
                      <div className="reference-panel__header">
                        <h2>{tableSectionTitle}</h2>
                        <button type="button" aria-label="More options">
                          <FaEllipsisH />
                        </button>
                      </div>

                      <div className="reference-table">
                        <div className="reference-table__head">
                          {tableColumnLabels.map(label => (
                            <span key={label}>{label}</span>
                          ))}
                        </div>

                        {isPosOnlyDashboard ? (
                          posLoading ? (
                            <div className="reference-loading reference-loading--table">
                              <Spinner animation="border" size="sm" />
                            </div>
                          ) : transactionRows.length === 0 ? (
                            <div className="reference-empty-table">{dashboardContent.tableEmptyState}</div>
                          ) : (
                            transactionRows.map(row => (
                              <div key={row.id} className="reference-table__row">
                                <div className="reference-table__identity">
                                  <span>{getInitials(row.name)}</span>
                                  <div>
                                    <strong>{row.name}</strong>
                                    <small>{row.sublabel}</small>
                                  </div>
                                </div>
                                <span>{row.middle}</span>
                                <span>{row.right}</span>
                              </div>
                            ))
                          )
                        ) : membersLoading ? (
                          <div className="reference-loading reference-loading--table">
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : customerRows.length === 0 ? (
                          <div className="reference-empty-table">No member data available for this outlet.</div>
                        ) : (
                          customerRows.map(row => (
                            <div key={row.id} className="reference-table__row">
                              <div className="reference-table__identity">
                                <span>{getInitials(row.name)}</span>
                                <div>
                                  <strong>{row.name}</strong>
                                  <small>{row.contact}</small>
                                </div>
                              </div>
                              <span>{row.outlet}</span>
                              <span>{row.contact}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </article>
                  </div>

                  <div className="reference-board__right">
                    <div className="reference-mini-panels">
                      {quickStatPanels.map(panel => {
                        const Icon = panel.icon;

                        return (
                          <article key={panel.label} className="reference-mini-panel">
                            <div className="reference-mini-panel__icon">
                              <Icon />
                            </div>
                            <small>{panel.label}</small>
                            <strong>{panel.value}</strong>
                            <span>{panel.caption}</span>
                          </article>
                        );
                      })}
                    </div>

                    <article className="reference-panel reference-panel--chart">
                      <div className="reference-panel__header">
                        <h2>{secondaryChartTitle}</h2>
                      </div>

                      <div className="reference-profit">
                        <div className="reference-profit__copy">
                          <span>{secondaryChartValue}</span>
                          <small>{secondaryChartCaption}</small>
                        </div>

                        <div className="reference-profit__chart">
                          {loading || (isPosOnlyDashboard && posLoading) ? (
                            <div className="reference-loading">
                              <Spinner animation="border" size="sm" />
                            </div>
                          ) : (
                            <Line
                              data={isPosOnlyDashboard ? posTrendChartData : trendChartData}
                              options={sharedTrendChartOptions}
                            />
                          )}
                        </div>
                      </div>
                    </article>

                    <article className="reference-plan-card">
                      <div className="reference-plan-card__badge">
                        {isPosOnlyDashboard ? dashboardContent.planBadgeLabel : featuredPlan?.name || 'Membership setup'}
                      </div>
                      <button type="button" className="reference-plan-card__more" aria-label="More options">
                        <FaEllipsisH />
                      </button>

                      <div className="reference-plan-card__price">
                        <strong>{isPosOnlyDashboard ? formatCurrency(posMonthlyRevenue) : featuredPlan ? formatCurrency(featuredPlan.price) : 'IDR 0'}</strong>
                        <span>
                          {isPosOnlyDashboard ? 'Revenue this month' : featuredPlan ? formatPlanDuration(featuredPlan.duration_days) : 'No plan'}
                          <br />
                          {isPosOnlyDashboard
                            ? `${formatNumber(posQueueCount)} ${dashboardContent.queueMetricTitle.toLowerCase()}`
                            : featuredPlan?.type || 'Configured from backend'}
                        </span>
                      </div>

                      <p>
                        {isPosOnlyDashboard
                          ? dashboardContent.planDescription
                          : featuredPlanDescription}
                      </p>

                      <div className="reference-plan-card__actions">
                        <Link
                          to={isPosOnlyDashboard ? posRailLink : featuredPlan ? '/membership-payment' : '/membership-setting'}
                          className="reference-plan-card__button"
                        >
                          {isPosOnlyDashboard
                            ? dashboardContent.analyticsActionLabel
                            : featuredPlan
                              ? `${formatNumber(featuredPlan.approved_sales)} approved`
                              : 'Create plan'}
                        </Link>
                        <span className="reference-plan-card__spark">
                          {isPosOnlyDashboard ? <FaChartLine /> : <FaDumbbell />}
                        </span>
                      </div>
                    </article>
                  </div>
                </div>
              </>
            )}
            </section>
          </main>

          <aside className="reference-rail">
            <section className="reference-rail__section">
              <div className="reference-rail__section-header">
                <div className="reference-rail__section-heading">
                  <h3>{isPosOnlyDashboard ? dashboardContent.railTitle : 'Notifications'}</h3>
                  <p>
                    {isPosOnlyDashboard
                      ? dashboardContent.railDescription
                      : 'Monitor live check-in updates and open the full attendance workspace.'}
                  </p>
                </div>
              </div>

              <div className="reference-rail__list">
                {isPosOnlyDashboard ? (
                  posLoading ? (
                    <div className="reference-loading">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : openPOSTransactions.length === 0 ? (
                    <div className="reference-rail__empty">{dashboardContent.railEmptyState}</div>
                  ) : (
                    openPOSTransactions.slice(0, 5).map(item => (
                      <article key={`pos-queue-${item.id}`} className="reference-rail__item">
                        <span className="reference-rail__item-icon is-info">
                          <FaClipboardCheck />
                        </span>
                        <div>
                          <strong>{dashboardContent.tableIdentityLabel} #{item.id}</strong>
                          <small>{item.cashier_name || 'Cashier belum tercatat'} • {item.payment_method || 'Payment pending'}</small>
                          <small>{formatCurrency(item.total || 0)}</small>
                        </div>
                      </article>
                    ))
                  )
                ) : (
                  notificationItems.length === 0 ? (
                    <div className="reference-rail__empty">No recent check-in notifications.</div>
                  ) : (
                    notificationItems.map(item => {
                      const Icon = getNotificationIcon(item.kind);

                      return (
                        <article key={`${item.id}-${item.occurred_at}`} className="reference-rail__item">
                          <span className={`reference-rail__item-icon is-${item.status || 'info'}`}>
                            <Icon />
                          </span>
                          <div>
                            <strong>
                              {item.title} {item.status === 'success' ? 'berhasil' : 'perlu perhatian'}
                            </strong>
                            <small>{item.detail}</small>
                            <small>
                              {formatRelativeTime(item.occurred_at, {
                                preferLocalWallClock: shouldPreferLocalWallClockForActivity(item.kind),
                              })}
                            </small>
                          </div>
                        </article>
                      );
                    })
                  )
                )}
              </div>

              {(isPosOnlyDashboard || canManageEmployeeModules || canViewCheckinReport) ? (
                <div className="reference-rail__section-footer">
                  {isPosOnlyDashboard ? (
                    <Link to={posRailLink} className="reference-rail__action-link is-primary">
                      {dashboardContent.analyticsActionLabel}
                    </Link>
                  ) : canViewCheckinReport ? (
                    <Link to="/reportcheckin" className="reference-rail__action-link is-primary">
                      Lihat selengkapnya
                    </Link>
                  ) : null}
                  {canManageEmployeeModules && !isPosOnlyDashboard ? (
                    <Link to="/karyawanmodules" className="reference-rail__action-link is-secondary">
                      Akses staf
                    </Link>
                  ) : null}
                </div>
              ) : null}
            </section>

            {!isEmployeeExperience && !isPosOnlyDashboard ? (
              <section className="reference-rail__section">
                <h3>Activities</h3>

                <div className="reference-activity-list">
                  {activityItems.length === 0 ? (
                    <div className="reference-rail__empty">No recent activity recorded yet.</div>
                  ) : (
                    activityItems.map(item => (
                      <article key={`${item.kind}-${item.occurred_at}-${item.title}`} className="reference-activity-item">
                        <span
                          className="reference-activity-item__dot"
                          style={{ backgroundColor: getActivityAccent(item.kind) }}
                        />
                        <div>
                          <strong>{item.title}</strong>
                          <small>{item.detail}</small>
                          <small>
                            {formatRelativeTime(item.occurred_at, {
                              preferLocalWallClock: shouldPreferLocalWallClockForActivity(item.kind),
                            })}
                          </small>
                        </div>
                      </article>
                    ))
                  )}
                </div>
              </section>
            ) : null}

            <section className="reference-rail__section reference-rail__section--contacts">
              <h3>Contacts of your managers</h3>

              <div className="reference-contact-list">
                {managerContacts.length === 0 ? (
                  <div className="reference-rail__empty">No active employee contacts available.</div>
                ) : (
                  managerContacts.map((contact, index) => (
                    <article
                      key={contact.id || `${contact.name}-${contact.phone}`}
                      className={`reference-contact-item ${index === 0 ? 'is-highlighted' : ''}`}
                    >
                      <div className="reference-contact-item__identity">
                        <span
                          className="reference-contact-item__avatar"
                          style={{ '--contact-accent': index === 0 ? '#d8ff63' : '#8cd5ff' }}
                        >
                          {getInitials(contact.name)}
                        </span>
                        <div>
                          <strong>{contact.name}</strong>
                          <small>{contact.role} · {contact.outlet_name}</small>
                        </div>
                      </div>

                      {contact.phone ? (
                        <div className="reference-contact-item__actions">
                          <FaCommentDots />
                          <FaPhoneAlt />
                        </div>
                      ) : (
                        <button type="button" className="reference-contact-item__more" aria-label="Contact options">
                          <FaEllipsisH />
                        </button>
                      )}
                    </article>
                  ))
                )}
              </div>
            </section>
          </aside>
	        </div>
	      </div>
	  );
	}

export default Dashboard;
